// @flow
import { useEffect, useContext, useMemo, useCallback, useState } from 'react';
import filter from 'ramda/es/filter';
import propEq from 'ramda/es/propEq';
import prop from 'ramda/es/prop';
import both from 'ramda/es/both';
import values from 'ramda/es/values';
import map from 'ramda/es/map';
import isEmpty from 'ramda/es/isEmpty';
import evolve from 'ramda/es/evolve';
import { useTranslation } from 'react-i18next';

import { api } from 'app/api';
import { type SellPoint } from 'app/common/components-next/sell-points-data';
import { GET_CAFETERIA_CUSTOMER_MENU_COMPLEXES_FREE } from 'app/common/api/requests/cafeteria/customer/menu/complexes/free';

import { Purchases, type State, type Purchase, type PurchaseItem } from '../../PurchasesApi';
import { OrderDataContext } from '../OrderData';


const useOnOpen = (
  open: boolean,
  onClose: () => void,
  currentSellPoint: SellPoint | null,
  complexesFreeParams: {
    date: string,
    customerId: number,
  },
): [Purchase | null, boolean, Object, Object] => {
  const { date, customerId } = complexesFreeParams;
  const { t } = useTranslation('cafeteria');
  // id текущей точки продаж
  const currentSellPointId = prop('id', currentSellPoint) || null;

  // процесс загрузки данных по бесплатным комплектам
  const [loadingComplexesFree, setLoadingComplexesFree] = useState(false);

  // данные по бесплатным комплектам { guid: true }
  // const [complexesFree, setComplexesFree] = useState({});

  // данные покупок
  const { request, loading, content, error, occur }: State = useContext(Purchases);

  // текущий заказ
  const { additionalFreeGuids, setAdditionalFreeGuids } = useContext(OrderDataContext);


  // покупки заказа
  const targetPurchase: Purchase | null = useMemo(() => {
    const { purchases } = content;
    // берем индивидуальные покупки совпадающие по точке продаж
    const matched = filter(
      both(propEq('individual', true), propEq('sale_point_id', currentSellPointId)),
      purchases,
    );

    return prop(0, values(matched)) || null;
  }, [content, currentSellPointId]);


  // комплекты из заказа
  const complexItems = useMemo(() => {
    const items: Array<PurchaseItem> = targetPurchase ? values(prop('items', targetPurchase)) : [];
    return filter(({ type }) => (type === 'complex' || type === 1), items);
  }, [targetPurchase]);


  // покупки с учетом бесплатных комплектов
  const orderConsideringFreeItems: Purchase | null = useMemo(() => {
    if (targetPurchase) {
      return evolve({
        items: map(({ guid, ...others }) => ({
          guid,
          ...others,
          free_for_sale: Boolean(additionalFreeGuids[guid]),
        })),
      }, targetPurchase);
    }
    return targetPurchase;
  }, [targetPurchase, additionalFreeGuids]);


  // запрос данных бесплатных комплектов
  const handleRequestGuidesFree = useCallback(async () => {
    if (!targetPurchase || isEmpty(complexItems)) return;
    const { guid: purchaseGuid } = targetPurchase;
    setLoadingComplexesFree(true);
    const complexGuids = map(prop('guid'), complexItems);

    const complexesFreeRes = await Promise.all(
      complexGuids.map(guid => api.request(GET_CAFETERIA_CUSTOMER_MENU_COMPLEXES_FREE, {
        error: t('menu_kits_fetch_free_error', 'Не удалось получить данные по бесплатным комплектам'),
        params: {
          date,
          customer_id: customerId,
          purchase_guid: purchaseGuid,
          complex_guid: guid,
        },
      })),
    );

    setLoadingComplexesFree(false);

    const a = complexesFreeRes.reduce((acc, val, i) => {
      if (val === true) {
        acc[complexGuids[i]] = true;
        return acc;
      }
      return acc;
    }, {});

    if (!isEmpty(a)) {
      setAdditionalFreeGuids(a);
    }
  }, [t, date, customerId, complexItems, targetPurchase, setAdditionalFreeGuids]);


  // запуск запроса бесплатных комплектов при изменении входящих данных
  useEffect(() => {
    handleRequestGuidesFree();
  }, [handleRequestGuidesFree]);


  // запрос данных заказа из покупок
  useEffect(() => {
    if (open) {
      request.purchases();
    }
  }, [open, request]);


  // закрываем форму, если не удалось получить данные заказа с сервера
  useEffect(() => {
    if (open && error) {
      onClose();
    }
  }, [onClose, error, open]);


  return [
    orderConsideringFreeItems,
    loading || loadingComplexesFree,
    request,
    occur,
  ];
};

export default useOnOpen;
